import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.REACT_APP_API_URL ?? 'http://localhost:8000',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add CSRF token to mutating requests
api.interceptors.request.use((config) => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method || '')) {
    const csrfToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('csrftoken='))
      ?.split('=')[1];
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
  }
  return config;
});

// Event the app listens for to reset auth state and fall back to the login
// screen when the server reports the session is gone. Module-scoped axios can't
// touch React state directly, so we bridge through a window event (see useAuth).
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

// DRF SessionAuthentication answers an UNAUTHENTICATED request with 403 (not
// 401) and exactly this NotAuthenticated detail. A genuine permission denial
// (intentional — ACTIVE-only script downloads, editor-gated writes) is a
// *different* 403 ("You do not have permission to perform this action.") whose
// caller already handles it. We match the canonical string EXACTLY (not a
// substring) so a future permission message that happens to mention
// "authentication credentials" can't masquerade as a dead session and log a
// clinician out mid-task.
const NOT_AUTHENTICATED_DETAIL = 'authentication credentials were not provided.';

// Auth endpoints whose own callers depend on the rejection: /me (useAuth.checkAuth
// clears its initial loading spinner and renders LoginPage on reject) and
// login/logout (LoginPage surfaces bad-credential errors). Excluded from the
// session-expired short-circuit. Matched against the exact request pathname, not
// a substring, so a future nested route (e.g. /api/accounts/me/notifications/)
// isn't silently swallowed too.
const AUTH_PATHS = [
  '/api/accounts/me',
  '/api/accounts/login',
  '/api/accounts/logout',
];

const stripTrailingSlash = (path: string): string => path.replace(/\/+$/, '');

function requestPathname(config: { url?: string; baseURL?: string } | undefined): string {
  const raw = config?.url ?? '';
  try {
    return new URL(raw, config?.baseURL || 'http://localhost').pathname;
  } catch {
    return raw.split('?')[0];
  }
}

// Trailing-slash-insensitive so the exclusion survives Django's APPEND_SLASH
// redirects and any future slashless caller. If a /me 403 ever slipped past this
// exclusion it would be swallowed by the never-settling promise below and hang
// checkAuth's loading spinner forever — so we normalize both sides.
function isAuthPath(config: { url?: string; baseURL?: string } | undefined): boolean {
  const path = stripTrailingSlash(requestPathname(config));
  return AUTH_PATHS.some((authPath) => stripTrailingSlash(authPath) === path);
}

// DRF's error JSON lives in `error.response.data` — normally a parsed object,
// but for `responseType: 'blob'` requests (RayStation/verification/xlsx
// downloads) axios leaves it as a Blob. Without reading the Blob, an expired
// session mid-download would be missed (the detail would read as undefined).
// Blob.text() does not consume the Blob, so a caller's own `.catch` can still
// read the body afterward to surface a permission message.
async function extractErrorDetail(response: { data?: unknown } | undefined): Promise<string | null> {
  const data = response?.data;
  if (data && typeof data === 'object' && !(data instanceof Blob)) {
    const detail = (data as { detail?: unknown }).detail;
    return typeof detail === 'string' ? detail : null;
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed && typeof parsed.detail === 'string') return parsed.detail;
    } catch {
      // not JSON / unreadable body — treat as no detail
    }
  }
  return null;
}

// Globally recover from an expired/absent session: fire the reset event and halt
// the promise chain so the in-flight page (about to unmount in favor of
// LoginPage) neither runs `.then` on undefined data nor leaks an unhandled
// rejection into Sentry. Exported for unit tests; registered below as the
// interceptor's rejection handler.
export async function handleResponseError(error: any): Promise<never> {
  const status = error?.response?.status;

  if (!isAuthPath(error?.config) && (status === 401 || status === 403)) {
    let sessionExpired = status === 401;
    if (status === 403) {
      const detail = await extractErrorDetail(error.response);
      sessionExpired =
        typeof detail === 'string' &&
        detail.trim().toLowerCase() === NOT_AUTHENTICATED_DETAIL;
    }
    if (sessionExpired) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      // Never settles: the app is resetting to the login screen, so this
      // request's chain is intentionally abandoned.
      return new Promise<never>(() => {});
    }
  }
  return Promise.reject(error);
}

api.interceptors.response.use((response) => response, handleResponseError);

export default api;
